/**
 * Le défilement du produit, et la préférence qu'il respecte.
 *
 * ## Le défaut que ce module ferme — 02/09/2026
 *
 * `prefers-reduced-motion: reduce` n'est pas un réglage d'esthétique. Il est
 * posé par des gens que le mouvement rend malades — troubles vestibulaires,
 * migraine avec aura — et le défilement animé d'une page entière est
 * exactement ce qu'il refuse. La règle non négociable n° 8 le nomme.
 *
 * Un seul endroit du produit le consultait : la remontée d'ancre au chargement,
 * dans `App.tsx`. Les trois autres — les liens de la barre, ceux du pied de
 * page, et le bouton « Comprendre l'exposition de mon équipe » de l'écran de
 * résultat — imposaient `behavior: "smooth"` sans rien demander.
 *
 * Les deux premiers partagent leur code, donc le défaut y était écrit une fois
 * et rendu trois : la barre et le pied de page appellent le même crochet. C'est
 * la forme ordinaire de ce genre d'oubli — une règle tenue à l'endroit où on y
 * a pensé, absente partout ailleurs.
 *
 * ## Pourquoi un module, pour trois lignes
 *
 * Parce que la question n'est pas « comment défiler » mais « qui décide de
 * l'animation », et qu'elle doit avoir une seule réponse. Le quatrième appel
 * qu'on écrira dans six mois lira ce fichier plutôt que de recopier le
 * troisième.
 */

/**
 * La personne demande-t-elle qu'on lui épargne le mouvement ?
 *
 * Défensif sur `matchMedia` : les recettes de ce paquet tournent sans DOM, et
 * un module qui explose à l'import serait une régression bien pire que le
 * défilement qu'il règle.
 */
export function sansAnimation(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Le comportement de défilement à appliquer, selon la préférence. */
export function comportementDefilement(): ScrollBehavior {
  return sansAnimation() ? "auto" : "smooth";
}

/**
 * Amène une section à l'écran, en respectant la préférence.
 *
 * Silencieux quand l'ancre n'existe pas : un lien de navigation qui lèverait
 * une erreur parce qu'une section n'est pas montée casserait la page pour un
 * geste qui ne devait rien casser.
 */
export function defilerVers(id: string): void {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: comportementDefilement(), block: "start" });
}

/**
 * Remonte en haut de page, en respectant la préférence.
 *
 * Utilisé au changement d'étape : un écran suivant qui s'ouvre au milieu de
 * lui-même fait croire qu'il ne s'est rien passé.
 */
export function remonter(): void {
  window.scrollTo({ top: 0, behavior: comportementDefilement() });
}
